import moment from "moment";
import { CleaningRouteAction } from "./action";
import { CleaningRouteState, cleaningRouteState } from "./state";

type RawRouteDetailItem = {
    cleaning_detail_route_id?: number;
    sequence?: number;
    time_start?: string | null;
    time_end?: string | null;
    value?: number;
    label?: string;
    __uniqueId?: string;
};

export function CleaningRouteReducer(
    state: CleaningRouteState = cleaningRouteState,
    action: CleaningRouteAction
): CleaningRouteState {
    switch (action.type) {
        case "@@cleaningRouteState/initCleaningRouteListAction":
            return {
                ...state,
                cleaningRouteList: action.cleaningRouteList,
            };
        case "@@cleaningRouteState/initSelectedCleaningRouteDetailAction": {
            const effective_dateConvert = moment.utc(
                action.cleaningRoute.effective_date
            );
            const r = action.cleaningRoute;
            const raw = action.selectedBinInfo;
            const initialRouteDetails = Array.isArray(raw)
                ? raw.map((item: RawRouteDetailItem, idx: number) => ({
                    cleaning_detail_route_id: Number(item.cleaning_detail_route_id ?? 0),
                    sequence: idx,
                    time_start: item.time_start != null ? String(item.time_start) : null,
                    time_end: item.time_end != null ? String(item.time_end) : null,
                    value: Number(item.value ?? 0),
                    label: String(item.label ?? ""),
                }))
                : null;
            const normalizedList = Array.isArray(raw)
                ? raw.map((item: RawRouteDetailItem, idx: number) => ({
                    ...item,
                    sequence: idx,
                    __uniqueId: item.__uniqueId || `${item.value}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}-${idx}`,
                }))
                : raw;
            return {
                ...state,
                selectedDetailRouteList: normalizedList as typeof raw,
                initialRouteDetails,
                route_name: action.cleaningRoute.route_name,
                effective_date: effective_dateConvert,
                remark: action.cleaningRoute.remark,
                weekday: action.cleaningRoute.weekday,
                mon: r.mon ?? 0,
                tue: r.tue ?? 0,
                wed: r.wed ?? 0,
                thu: r.thu ?? 0,
                fri: r.fri ?? 0,
                sat: r.sat ?? 0,
                sun: r.sun ?? 0,
                holiday: r.holiday !== undefined && r.holiday !== null ? r.holiday : null,
                cleaner_id: action.cleaningRoute.user_id,
                route_cleaning_id: action.cleaningRoute.route_cleaning_id,
            };
        }

        case "@@cleaningRouteState/changeCleaningDetailRouteAction": {
            const next = action.selectedBinInfo;
            if (!next || !Array.isArray(next)) {
                return { ...state, selectedDetailRouteList: next };
            }
            const prev = state.selectedDetailRouteList;
            const prevByUniqueId =
                prev && Array.isArray(prev)
                    ? new Map(
                          prev.map((item) => [
                              item.__uniqueId || String(item.value),
                              item as typeof item & RawRouteDetailItem,
                          ])
                      )
                    : new Map();
            const merged = next.map((item, index) => {
                const uniqueKey = item.__uniqueId || String(item.value);
                const existing = prevByUniqueId.get(uniqueKey);
                if (!existing) {
                    return { ...item, sequence: index };
                }
                return {
                    ...item,
                    cleaning_detail_route_id: existing.cleaning_detail_route_id,
                    sequence: index,
                    time_start: existing.time_start ?? item.time_start,
                    time_end: existing.time_end ?? item.time_end,
                };
            });
            return {
                ...state,
                selectedDetailRouteList: merged,
            };
        }
        case "@@cleaningRouteState/changeCleaningRouteNameAction":
            return {
                ...state,
                route_name: action.route_name,
            };
        case "@@cleaningRouteState/handleEffectiveDateChangeAction":
            return {
                ...state,
                effective_date: action.effective_date,
            };
        case "@@cleaningRouteState/handleCleanerChangeAction":
            return {
                ...state,
                cleaner_id: action.cleaner_id,
            };

        case "@@cleaningRouteState/handlCleaningRouteWeekdayChange":
            return {
                ...state,
                weekday: action.weekday,
            };
        case "@@cleaningRouteState/handlCleaningRouteRemarksChangeAction":
            return {
                ...state,
                remark: action.remarks,
            };
        case "@@cleaningRouteState/handleCleaningRouteStartTimeAction":
            const target_bin = state.selectedDetailRouteList![action.index];
            if (target_bin) {
                const updatedBin = {
                    ...target_bin,
                    time_start: action.start_time,
                };

                const updatedSelectedBinInfo =
                    state.selectedDetailRouteList?.map((bin, index) =>
                        index === action.index ? updatedBin : bin
                    );
                return {
                    ...state,
                    selectedDetailRouteList: updatedSelectedBinInfo!,
                };
            }
            return state;

        case "@@cleaningRouteState/handleCleaningRouteEndTimeAction":
            const _target_bin = state.selectedDetailRouteList![action.index];
            if (_target_bin) {
                const updatedBin = {
                    ..._target_bin,
                    time_end: action.end_time,
                };

                const updatedSelectedBinInfo =
                    state.selectedDetailRouteList?.map((bin, index) =>
                        index === action.index ? updatedBin : bin
                    );
                return {
                    ...state,
                    selectedDetailRouteList: updatedSelectedBinInfo!,
                };
            }
            return state;

        case "@@cleaningRouteState/setHolidayAction":
            return {
                ...state,
                holiday: action.holiday,
            };
        case "@@cleaningRouteState/setWeekdayFlagsAction":
            return {
                ...state,
                mon: action.payload.mon ?? state.mon,
                tue: action.payload.tue ?? state.tue,
                wed: action.payload.wed ?? state.wed,
                thu: action.payload.thu ?? state.thu,
                fri: action.payload.fri ?? state.fri,
                sat: action.payload.sat ?? state.sat,
                sun: action.payload.sun ?? state.sun,
            };
        case "@@cleaningRouteState/handlClearCleaningRouteAction":
            return {
                ...state,
                selectedDetailRouteList: null,
                initialRouteDetails: null,
                route_name: null,
                effective_date: null,
                weekday: null,
                mon: 0,
                tue: 0,
                wed: 0,
                thu: 0,
                fri: 0,
                sat: 0,
                sun: 0,
                holiday: null,
                cleaner_id: null,
                remark: null,
                route_cleaning_id: null,
            };

        case "@@cleaningRouteState/setInitialRouteDetailsAction":
            return {
                ...state,
                initialRouteDetails: action.initialRouteDetails,
            };

        default:
            return state;
    }
}
